/* import __COLOCATED_TEMPLATE__ from './seller-postings-page-results-item.hbs'; */
import { computed } from '@ember/object';
import { equal, notEmpty, readOnly, bool } from '@ember/object/computed';
import Component from '@ember/component';
import { inject as service } from '@ember/service';
import moment from 'moment';
import { ASSIGNMENT } from '../../models/assignment';
import { stripTags, htmlEntitiesDecode } from '../../utils/misc';
import { task } from 'ember-concurrency';

const MAX_LISTED_DELIVERABLES = 5;
const MAX_LISTED_SCHEDULE_ITEMS = 5;

/** @module */
const SellerPostingsPageResultsItem = Component.extend({
  applicationStateManager: service('state-managers/application'),
  errorTracker: service('error-tracker'),

  /** @inheritDoc */
  tagName: '',

  /** @type {Ember.ComputedProperty} */
  perfectMatch: equal('data.freelancerMatch', 'perfect'),

  /** @type {boolean} */
  unread: true,

  /** @type {Ember.ComputedProperty} */
  variant: computed('unread', function() {
    return !this.get('unread') ? 'gray' : undefined;
  }),

  /** @type {DS.Model} */
  data: null,

  /** @type {Ember.ComputedProperty} */
  hasApplied: notEmpty('data.applications.[]'),

  /** @type {Ember.ComputedProperty} */
  appliedText: computed('data.applications.[]', function() {
    const applications = this.get('data.applications');
    const count = applications.get('length');

    switch (count) {
      case 0:
        return '';

      case 1: {
        const application = applications.get('firstObject');
        return `Applied ${moment(application.createdAt).format('l')}`;
      }

      default:
        return `Applied ${count} times`;
    }
  }),

  /** @type {Ember.ComputedProperty} */
  isSelect: readOnly('data.select'),

  /** @type {Ember.ComputedProperty} */
  isCallForPitches: computed('data.type', function() {return this.get('data.type') === ASSIGNMENT.TYPES.PITCH;}),

  /** @type {Ember.ComputedProperty} */
  companyLogo: readOnly('data.buyer.company.image.transforms.profile128'),

  /** @type {Ember.ComputedProperty} */
  companyName: computed('data.buyer.company.name', 'data.brand.displayedName', function() {
    const { brand } = this.data;

    if (brand) {
      return brand.displayedName;
    }

    return htmlEntitiesDecode(this.data.buyer.company.name);
  }),

  /** @type {Ember.ComputedProperty} */
  description: computed('data.description', function() {
    const desc = stripTags(this.get('data.description'));

    // this will clamp at either 4 lines (in css), or 375 characters, whichever comes first
    return desc.length > 375 ? desc.substr(0, 375) + '…' : desc;
  }),

  /** @type {boolean} */
  isFavorite: false,

  /** @type {boolean} */
  hasFocus: false,

  /** @type {Ember.ComputedProperty} */
  hasFeedback: bool('data.assignmentUserMeta.notInterested'),

  /** @type {Ember.ComputedProperty} */
  notInterestedText: readOnly('data.assignmentUserMeta.notInterestedReasonString'),

  /** @type {Ember.ComputedProperty} */
  location: readOnly('data.locationText'),

  /** @type {Ember.ComputedProperty} */
  rate: readOnly('data.costText'),

  /** @type {Ember.ComputedProperty} */
  title: computed('data.titleWithUrgent', function() {
    return htmlEntitiesDecode(this.data.titleWithUrgent).trim();
  }),

  /** @type {number} */
  maxListedDeliverables: MAX_LISTED_DELIVERABLES,

  /** @type {number} */
  maxListedScheduleItems: MAX_LISTED_SCHEDULE_ITEMS,

  setFavoriteTask: task(function *(assignment, isFavorite) {
    // show as updated immediately
    this.set('isFavorite', !isFavorite);

    try {
      yield this.setFavorite(assignment, !isFavorite);
    } catch(e) {
      // revert state on error
      this.set('isFavorite', isFavorite);

      this.errorTracker.error('Failed set toggle a postings saved status');

      const saveWord = isFavorite
        ? 'unsave'
        : 'save';

      this.applicationStateManager.addToast(`Failed to ${saveWord} "${this.title}". Try again later.`);
    }
  }).drop(),

  setFeedbackTask: task(function *(assignment) {
    try {
      yield this.showFeedback(assignment);
    } catch(e) {
      this.errorTracker.error('Failed to dislike posting');

      this.applicationStateManager.addToast(`Failed to dislike "${this.title}". Try again later.`);
    }
  }).drop(),

  /** @inheritDoc */
  actions: {
    /**
     *
     */
    onclick() {
      this.get('onclick')();
    },

    onkeydown(e) {
      if (e.defaultPrevented) {
        return;
      }

      switch (e.key) {
        case 'Enter':
          this.onclick();

          break;
      }
    },

    /**
     * @param {boolean} favorite
     * @param {MouseEvent} e
     */
    setFavorite(favorite, e) {
      e.stopPropagation();

      this.setFavoriteTask.perform(this.data, favorite);
    },

    /**
     * @param {MouseEvent} e
     */
    showFeedback(e) {
      e.stopPropagation();

      this.setFeedbackTask.perform(this.data);
    },

    /**
     * @param {KeyboardEvent} e
     */
    preventContainerKeydown(e) {
      e.stopPropagation();
    }
  },

  didInsertElement() {
    this._super(...arguments);

    this.set('isFavorite', Boolean(this.data.assignmentUserMeta?.favorite));
  },
});

export default SellerPostingsPageResultsItem;
